<template>
  <v-card class="google-sync-card">
    <v-card-title class="headline d-flex align-center">
      <v-spacer></v-spacer>
      <v-btn
        :color="isAuthorized ? 'success' : 'primary'"
        @click="isAuthorized ? showSyncOptionsDialog() : authorizeGoogle()"
        :loading="isLoading"
        class="mr-2"
        large
      >
        <v-icon left>
          {{ isAuthorized ? "mdi-sync" : "mdi-google" }}
        </v-icon>
        {{ isAuthorized ? "Sync Now" : "Connect Google Calendar" }}
      </v-btn>

      <v-menu v-if="isAuthorized" offset-y>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            color="primary"
            outlined
            v-bind="attrs"
            v-on="on"
            :loading="isMenuLoading"
            large
          >
            <v-icon>mdi-dots-vertical</v-icon>
          </v-btn>
        </template>
        <v-list>
          <v-list-item @click="showForceSyncDialog = true">
            <v-list-item-icon>
              <v-icon color="success">mdi-sync-circle</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>Force Sync Options</v-list-item-title>
              <v-list-item-subtitle>Re-sync all or current month events</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
          
          <v-divider></v-divider>
          
          <v-list-item @click="showUnsyncDialog = true">
            <v-list-item-icon>
              <v-icon color="warning">mdi-sync-off</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>Unsync All Events</v-list-item-title>
              <v-list-item-subtitle>Reset sync status for all events</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>

          <v-list-item @click="refreshStats">
            <v-list-item-icon>
              <v-icon color="info">mdi-information</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>Refresh Sync Status</v-list-item-title>
              <v-list-item-subtitle>Update sync statistics</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-card-title>

    <v-card-text>
      <v-alert v-if="isAuthorized" type="success" dense class="mb-0">
        <div class="d-flex align-center">
          <span>Connected to Google Calendar</span>
        </div>
      </v-alert>
      <v-alert v-else type="info" dense class="mb-0">
        <div class="d-flex align-center">
          <span>Connect your Google Calendar to automatically sync events</span>
        </div>
      </v-alert>

      <!-- Sync Statistics -->
      <div v-if="isAuthorized && syncStats.totalEvents > 0" class="mt-3">
        <v-row class="text-center">
          <v-col cols="3">
            <div class="text-h6 primary--text">{{ syncStats.totalEvents }}</div>
            <div class="caption">Total Events</div>
          </v-col>
          <v-col cols="3">
            <div class="text-h6 success--text">{{ syncStats.syncedEvents }}</div>
            <div class="caption">Synced</div>
          </v-col>
          <v-col cols="3">
            <div class="text-h6 warning--text">{{ syncStats.unsyncedEvents }}</div>
            <div class="caption">Unsynced</div>
          </v-col>
          <v-col cols="3">
            <div class="text-h6 info--text">{{ syncStats.currentMonthEvents || 0 }}</div>
            <div class="caption">This Month</div>
          </v-col>
        </v-row>
        
        <div v-if="syncStats.lastSyncDate" class="text-center caption mt-2 grey--text">
          Last sync: {{ formatDate(syncStats.lastSyncDate) }}
        </div>
      </div>
    </v-card-text>

    <v-dialog v-model="showSyncOptions" max-width="500" persistent>
      <v-card>
        <v-card-title class="headline primary--text">
          <v-icon color="primary" class="mr-2">mdi-sync</v-icon>
          Sync Options
        </v-card-title>
        
        <v-card-text>
          <p class="mb-4">Choose which events to sync to Google Calendar:</p>
          
          <v-radio-group v-model="syncOption" class="mt-4">
            <v-radio
              label="Sync unsynced events (Current month only)"
              value="current"
              color="primary"
            >
              <template v-slot:label>
                <div>
                  <div class="font-weight-medium">Current month only</div>
                  <div class="caption text--secondary">
                    Sync {{ syncStats.currentMonthUnsynced || 0 }} unsynced events from {{ currentMonthName }}
                  </div>
                </div>
              </template>
            </v-radio>
            
            <v-radio
              label="Sync all unsynced events"
              value="all"
              color="primary"
            >
              <template v-slot:label>
                <div>
                  <div class="font-weight-medium">All unsynced events</div>
                  <div class="caption text--secondary">
                    Sync all {{ syncStats.unsyncedEvents }} unsynced events across all dates
                  </div>
                </div>
              </template>
            </v-radio>
          </v-radio-group>
          
          <v-alert v-if="syncOption === 'all'" type="info" dense class="mt-4">
            This may take longer for large numbers of events.
          </v-alert>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showSyncOptions = false" :disabled="isLoading">
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            @click="handleSyncWithOptions"
            :loading="isLoading"
          >
            <v-icon left>mdi-sync</v-icon>
            Start Sync
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showForceSyncDialog" max-width="500" persistent>
      <v-card>
        <v-card-title class="headline warning--text">
          <v-icon color="warning" class="mr-2">mdi-sync-circle</v-icon>
          Force Sync Options
        </v-card-title>
        
        <v-card-text>
          <p class="mb-4">Force sync will re-sync events that are already synced. Choose scope:</p>
          
          <v-radio-group v-model="forceSyncOption" class="mt-4">
            <v-radio
              label="Force sync current month events"
              value="current"
              color="warning"
            >
              <template v-slot:label>
                <div>
                  <div class="font-weight-medium">Current month only</div>
                  <div class="caption text--secondary">
                    Re-sync all events from {{ currentMonthName }} ({{ syncStats.currentMonthEvents || 0 }} events)
                  </div>
                </div>
              </template>
            </v-radio>
            
            <v-radio
              label="Force sync all events"
              value="all"
              color="warning"
            >
              <template v-slot:label>
                <div>
                  <div class="font-weight-medium">All events</div>
                  <div class="caption text--secondary">
                    Re-sync all {{ syncStats.totalEvents }} events (may take several minutes)
                  </div>
                </div>
              </template>
            </v-radio>
          </v-radio-group>
          
          <v-alert type="warning" dense class="mt-4">
            <strong>Warning:</strong> This will update already synced events in Google Calendar.
            {{ forceSyncOption === 'all' ? 'This operation may take several minutes.' : '' }}
          </v-alert>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showForceSyncDialog = false" :disabled="isMenuLoading">
            Cancel
          </v-btn>
          <v-btn
            color="warning"
            @click="handleForceSyncWithOptions"
            :loading="isMenuLoading"
          >
            <v-icon left>mdi-sync-circle</v-icon>
            Force Sync
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showUnsyncDialog" max-width="600" persistent>
      <v-card>
        <v-card-title class="headline error--text">
          <v-icon color="error" class="mr-2">mdi-alert</v-icon>
          Unsync Calendar Events
        </v-card-title>
        
        <v-card-text>
          <p class="mb-4">
            You are about to unsync <strong>{{ syncStats.syncedEvents }}</strong> events from Google Calendar.
            Choose how you want to proceed:
          </p>
          
          <v-radio-group v-model="unsyncOption" class="mt-4">
            <v-radio
              label="Reset sync status only"
              value="reset"
              color="warning"
            >
              <template v-slot:label>
                <div>
                  <div class="font-weight-medium">Reset sync status only</div>
                  <div class="caption text--secondary">
                    Events will remain in your Google Calendar but won't be managed by this system
                  </div>
                </div>
              </template>
            </v-radio>
            
            <v-radio
              label="Reset and delete from Google Calendar"
              value="delete"
              color="error"
            >
              <template v-slot:label>
                <div>
                  <div class="font-weight-medium error--text">Reset and delete from Google Calendar</div>
                  <div class="caption error--text">
                    <strong>Warning:</strong> This will permanently delete all synced events from your Google Calendar!
                  </div>
                </div>
              </template>
            </v-radio>
          </v-radio-group>
          
          <v-alert v-if="unsyncOption === 'delete'" type="error" dense class="mt-4">
            <strong>This action cannot be undone!</strong> 
            All {{ syncStats.syncedEvents }} synced events will be permanently deleted from your Google Calendar.
          </v-alert>

          <v-alert v-else type="warning" dense class="mt-4">
            Events will remain in Google Calendar but sync status will be reset. 
            You can re-sync them later if needed.
          </v-alert>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showUnsyncDialog = false" :disabled="isMenuLoading">
            Cancel
          </v-btn>
          <v-btn
            :color="unsyncOption === 'delete' ? 'error' : 'warning'"
            @click="handleUnsyncAll"
            :loading="isMenuLoading"
          >
            <v-icon left>
              {{ unsyncOption === 'delete' ? 'mdi-delete' : 'mdi-sync-off' }}
            </v-icon>
            {{ unsyncOption === 'delete' ? 'Delete & Unsync' : 'Unsync Events' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showSyncProgress" max-width="500" persistent>
      <v-card>
        <v-card-title>
          <v-icon class="mr-2" :color="syncProgress.error ? 'error' : 'primary'">
            {{ syncProgress.error ? 'mdi-alert-circle' : 'mdi-sync' }}
          </v-icon>
          {{ syncProgress.title }}
        </v-card-title>
        
        <v-card-text>
          <div v-if="!syncProgress.completed">
            <v-progress-linear indeterminate color="primary" class="mb-4"></v-progress-linear>
            <p>{{ syncProgress.message }}</p>
            
            <div v-if="syncProgress.estimatedTime" class="caption grey--text mt-2">
              Estimated time: {{ syncProgress.estimatedTime }}
            </div>
          </div>
          
          <div v-else>
            <v-alert :type="syncProgress.error ? 'error' : 'success'" dense>
              {{ syncProgress.message }}
            </v-alert>
            
            <div v-if="syncProgress.results && !syncProgress.error" class="mt-3">
              <div class="subtitle-2 mb-2">Sync Results:</div>
              <v-simple-table dense>
                <tbody>
                  <tr>
                    <td>Total Processed:</td>
                    <td class="text-right font-weight-bold">{{ syncProgress.results.total || 0 }}</td>
                  </tr>
                  <tr>
                    <td>Successful:</td>
                    <td class="text-right font-weight-bold success--text">{{ syncProgress.results.successful || 0 }}</td>
                  </tr>
                  <tr v-if="syncProgress.results.failed > 0">
                    <td>Failed:</td>
                    <td class="text-right font-weight-bold error--text">{{ syncProgress.results.failed }}</td>
                  </tr>
                  <tr v-if="syncProgress.results.scope">
                    <td>Scope:</td>
                    <td class="text-right font-weight-bold info--text">{{ scopeDisplayName(syncProgress.results.scope) }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </div>
          </div>
        </v-card-text>
        
        <v-card-actions v-if="syncProgress.completed">
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="showSyncProgress = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import axios from "axios";
import moment from "moment";

const url = require("../../client_config/config").url;

export default {
  name: "GoogleCalendarSync",
  
  props: {
    initialAuthState: {
      type: Boolean,
      default: false
    },
    currentDateRange: {
      type: Object,
      default: () => ({
        start: moment().startOf('month').format('YYYY-MM-DD'),
        end: moment().endOf('month').format('YYYY-MM-DD')
      })
    }
  },

  data() {
    return {
      isLoading: false,
      isMenuLoading: false,
      isAuthorized: this.initialAuthState,
      showSyncOptions: false,
      showForceSyncDialog: false,
      showUnsyncDialog: false,
      showSyncProgress: false,
      syncOption: 'current',
      forceSyncOption: 'current',
      unsyncOption: 'reset',
      syncProgress: {
        title: '',
        message: '',
        completed: false,
        error: false,
        results: null,
        estimatedTime: null
      },
      syncStats: {
        totalEvents: 0,
        syncedEvents: 0,
        unsyncedEvents: 0,
        currentMonthEvents: 0,
        currentMonthUnsynced: 0,
        lastSyncDate: null
      }
    };
  },

  computed: {
    currentMonthName() {
      return moment().format('MMMM YYYY');
    }
  },

  mounted() {
    this.checkAuthorization();
    if (this.$route.query.code) {
      this.handleAuthCallback();
    }
  },

  methods: {
    showSyncOptionsDialog() {
      this.showSyncOptions = true;
    },

    async handleSyncWithOptions() {
      try {
        this.isLoading = true;
        this.showSyncOptions = false;
        this.showSyncProgress = true;

        const isCurrentMonth = this.syncOption === 'current';
        const eventCount = isCurrentMonth ? this.syncStats.currentMonthUnsynced : this.syncStats.unsyncedEvents;
        
        this.syncProgress = {
          title: 'Syncing Calendar',
          message: `Syncing ${isCurrentMonth ? 'current month' : 'all'} unsynced events to Google Calendar...`,
          completed: false,
          error: false,
          results: null,
          estimatedTime: this.getEstimatedTime(eventCount)
        };

        const payload = {
          scope: this.syncOption,
          dateRange: isCurrentMonth ? this.getCurrentMonthRange() : null
        };

        const response = await axios.post(`${url}/google/auth/sync-google-calendar`, payload);
        
        if (response.data.success) {
          const summary = response.data.summary;
          
          this.syncProgress = {
            title: 'Sync Completed',
            message: response.data.message || "Calendar synced successfully",
            completed: true,
            error: false,
            results: { ...summary, scope: this.syncOption },
            estimatedTime: null
          };
          
          await this.getSyncStats();
          this.$emit('sync-completed');
          
          setTimeout(() => {
            this.showSyncProgress = false;
            this.$emit('show-success', response.data.message || "Calendar synced successfully");
          }, 3000);
        }
      } catch (error) {
        console.error("Sync error:", error);
        this.syncProgress = {
          title: 'Sync Failed',
          message: error.response?.data?.message || "Failed to sync calendar",
          completed: true,
          error: true,
          results: null,
          estimatedTime: null
        };
        
        setTimeout(() => {
          this.showSyncProgress = false;
          this.$emit('show-error', error.response?.data?.message || "Failed to sync calendar");
        }, 3000);
      } finally {
        this.isLoading = false;
      }
    },

    async handleForceSyncWithOptions() {
      try {
        this.isMenuLoading = true;
        this.showForceSyncDialog = false;
        this.showSyncProgress = true;

        const isCurrentMonth = this.forceSyncOption === 'current';
        const eventCount = isCurrentMonth ? this.syncStats.currentMonthEvents : this.syncStats.totalEvents;
        
        this.syncProgress = {
          title: 'Force Syncing Calendar',
          message: `Re-syncing ${isCurrentMonth ? 'current month' : 'all'} events to Google Calendar...`,
          completed: false,
          error: false,
          results: null,
          estimatedTime: this.getEstimatedTime(eventCount, true)
        };

        const payload = {
          scope: this.forceSyncOption,
          dateRange: isCurrentMonth ? this.getCurrentMonthRange() : null
        };

        const response = await axios.post(`${url}/google/auth/force-sync`, payload);
        
        if (response.data.success) {
          const summary = response.data.summary;
          
          this.syncProgress = {
            title: 'Force Sync Completed',
            message: summary 
              ? `Force sync completed: ${summary.successful} successful, ${summary.failed} failed`
              : "Force sync completed successfully",
            completed: true,
            error: false,
            results: { ...summary, scope: this.forceSyncOption },
            estimatedTime: null
          };
          
          await this.getSyncStats();
          this.$emit('sync-completed');
          
          setTimeout(() => {
            this.showSyncProgress = false;
            this.$emit('show-success', "Force sync completed successfully");
          }, 3000);
        }
      } catch (error) {
        console.error("Force sync error:", error);
        this.syncProgress = {
          title: 'Force Sync Failed',
          message: error.response?.data?.message || "Failed to force sync calendar",
          completed: true,
          error: true,
          results: null,
          estimatedTime: null
        };
        
        setTimeout(() => {
          this.showSyncProgress = false;
          this.$emit('show-error', error.response?.data?.message || "Failed to force sync calendar");
        }, 3000);
      } finally {
        this.isMenuLoading = false;
      }
    },

    getCurrentMonthRange() {
      return {
        start: moment().startOf('month').format('YYYY-MM-DD'),
        end: moment().endOf('month').format('YYYY-MM-DD')
      };
    },

    getEstimatedTime(eventCount, isForceSync = false) {
      if (eventCount === 0) return null;
      
      const secondsPerEvent = isForceSync ? 3 : 2;
      const totalSeconds = eventCount * secondsPerEvent;
      
      if (totalSeconds < 60) {
        return `${totalSeconds} seconds`;
      } else if (totalSeconds < 3600) {
        return `${Math.ceil(totalSeconds / 60)} minutes`;
      } else {
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.ceil((totalSeconds % 3600) / 60);
        return `${hours}h ${minutes}m`;
      }
    },

    scopeDisplayName(scope) {
      switch (scope) {
        case 'current':
          return 'Current Month';
        case 'all':
          return 'All Events';
        default:
          return scope;
      }
    },

    async getSyncStats() {
      try {
        const currentMonthRange = this.getCurrentMonthRange();
        
        const response = await axios.get(`${url}/google/auth/sync-stats`, {
          params: {
            includeCurrentMonth: true,
            currentMonthStart: currentMonthRange.start,
            currentMonthEnd: currentMonthRange.end
          }
        });
        
        if (response.data.success) {
          this.syncStats = {
            totalEvents: response.data.stats.totalEvents,
            syncedEvents: response.data.stats.syncedEvents,
            unsyncedEvents: response.data.stats.unsyncedEvents,
            currentMonthEvents: response.data.stats.currentMonthEvents || 0,
            currentMonthUnsynced: response.data.stats.currentMonthUnsynced || 0,
            lastSyncDate: response.data.stats.lastSyncDate
          };
          
          this.$emit('stats-updated', this.syncStats);
        }
      } catch (error) {
        console.error("Error getting sync stats:", error);
      }
    },

    async checkAuthorization() {
      try {
        this.isLoading = true;
        const response = await axios.get(`${url}/google/auth/check-auth`);
        this.isAuthorized = response.data.isAuthorized;

        if (!response.data.success && response.data.message) {
          this.$emit('show-error', response.data.message);
        }

        if (this.isAuthorized) {
          await this.getSyncStats();
        }

        this.$emit('auth-changed', this.isAuthorized);
      } catch (error) {
        console.error("Authorization check error:", error);
        this.$emit('show-error', "Failed to check authorization status");
      } finally {
        this.isLoading = false;
      }
    },

    async handleAuthCallback() {
      const code = this.$route.query.code;
      if (!code) return;

      try {
        this.isLoading = true;
        this.showSyncProgress = true;
        this.syncProgress = {
          title: 'Connecting to Google Calendar',
          message: 'Processing authentication...',
          completed: false,
          error: false,
          results: null
        };

        const response = await axios.post(`${url}/google/auth/callback`, {
          code,
        });

        if (response.data.success) {
          this.isAuthorized = true;
          await this.$router.replace({ name: "Calendar New" });
          
          this.syncProgress = {
            title: 'Connection Successful',
            message: 'Successfully connected to Google Calendar',
            completed: true,
            error: false,
            results: null
          };
          
          setTimeout(() => {
            this.showSyncProgress = false;
            this.$emit('show-success', "Successfully connected to Google Calendar");
          }, 2000);
          
          await this.getSyncStats();
          this.$emit('auth-changed', true);
          this.$emit('sync-completed');
        }
      } catch (error) {
        this.syncProgress = {
          title: 'Connection Failed',
          message: error.response?.data?.message || 'Failed to connect to Google Calendar',
          completed: true,
          error: true,
          results: null
        };
        
        setTimeout(() => {
          this.showSyncProgress = false;
          this.$emit('show-error', "Failed to connect to Google Calendar");
        }, 3000);
      } finally {
        this.isLoading = false;
      }
    },

    async authorizeGoogle() {
      try {
        this.isLoading = true;
        const response = await axios.get(`${url}/google/auth/auth-url`);
        localStorage.setItem("calendarRedirectUrl", window.location.pathname);
        window.location.href = response.data.authUrl;
      } catch (error) {
        console.error("Authorization error:", error);
        this.$emit('show-error', "Failed to start Google authorization");
        this.isLoading = false;
      }
    },

    async handleUnsyncAll() {
      try {
        this.isMenuLoading = true;
        const deleteFromGoogle = this.unsyncOption === 'delete';
        
        const response = await axios.post(`${url}/google/auth/unsync-all`, {
          deleteFromGoogle: deleteFromGoogle
        });
        
        if (response.data.success) {
          const results = response.data.results;
          const message = results?.unsyncedCount 
            ? `Successfully unsynced ${results.unsyncedCount} events${deleteFromGoogle ? ' and deleted from Google Calendar' : ''}`
            : response.data.message;
          
          await this.getSyncStats();
          this.$emit('sync-completed');
          this.$emit('show-success', message);
        }
      } catch (error) {
        console.error("Unsync error:", error);
        this.$emit('show-error', error.response?.data?.message || "Failed to unsync events");
      } finally {
        this.isMenuLoading = false;
        this.showUnsyncDialog = false;
        this.unsyncOption = 'reset'; 
      }
    },

    async unsyncSingleEvent(eventId) {
      try {
        const response = await axios.post(`${url}/google/auth/unsync-event/${eventId}`, {
          deleteFromGoogle: false 
        });
        
        if (response.data.success) {
          await this.getSyncStats();
          this.$emit('sync-completed');
          this.$emit('show-success', "Event unsynced successfully");
          return true;
        }
        return false;
      } catch (error) {
        console.error("Unsync single event error:", error);
        this.$emit('show-error', error.response?.data?.message || "Failed to unsync event");
        return false;
      }
    },

    refreshStats() {
      this.getSyncStats();
    },

    formatDate(date) {
      if (!date) return '';
      return moment(date).format('MMM DD, YYYY HH:mm');
    }
  },

  watch: {
    isAuthorized(newVal) {
      if (newVal) {
        this.getSyncStats();
      }
    },
    
    currentDateRange: {
      handler() {
        if (this.isAuthorized) {
          this.getSyncStats();
        }
      },
      deep: true
    }
  }
};
</script>

<style lang="scss" scoped>
.google-sync-card {
  .v-btn {
    &.v-btn--loading {
      pointer-events: none;
      
      .v-btn__content {
        opacity: 0.6;
      }
    }
  }

  .v-alert {
    &.v-alert--dense {
      padding: 12px 16px;
      
      .v-icon {
        margin-right: 8px;
      }
    }
  }

  .v-radio-group {
    .v-radio {
      margin-bottom: 16px;
    }
  }
}
</style>